import store from "@/store";
import DcbService from "@/services/dcbService";
import getUserSessionData from "@/functions/getUserSessionData";
import {handleActionClick} from "./handleActionClick";
import {Brands} from "../brand/brandConfig";
import {ServiceIds} from "../config";
import {injectScript} from "@/functions/injectTellium";
import getServiceId from "@/functions/getServiceId";
import hasGameAccess from "@/functions/hasGameAccess";
import {environmentConfig} from "@/config/enviromentConfig";

const TPF_HOSTED_ROUTES = ['RequestOtp', 'SubmitOtp', 'SubscriptionView', 'ConfirmSubscription'];
const SUBSCRIPTION_ID_PARAMS = ['subscription-id', 'subscriptionId'];

function getSubscriptionIdParam(params) {
    for (const key of SUBSCRIPTION_ID_PARAMS) {
        const value = params.get(key);
        if (value) return value;
    }
    return undefined;
}

export async function userAuthenticate(to, from, next) {
    if (to.name === "RequestOtp" || !store.state.user) {
        try {
            await injectScript();
            console.log('Injected Telium and script loaded successfully');
            // You can place additional code here that should run after the script has loaded
        } catch (error) {
            console.error('Failed to load the script:', error);
        }
    }
    const {brand} = store.state.brandConfig;
    const params = new URLSearchParams(window.location.search);
    const subscriptionId = getSubscriptionIdParam(params);

    if (subscriptionId && !store.state.user) {
        return await subscriptionIdAuth(subscriptionId, next, params.get('campaignId') || params.get('cid'));
    }

    if (TPF_HOSTED_ROUTES.includes(to.name) && !subscriptionId && !params.has('e-msisdn')) {
        window.location.assign(environmentConfig.tpfHost);
        return next(false);
    }

    if (brand === Brands.VodacomEvinaSouthAfricaEnglish) await userAuthenticateEncryptedFlow(to, from, next)
    else await userAuthenticateNormalFlow(to, from, next);
}

async function userAuthenticateEncryptedFlow(to, from, next) {
    if (store.state.user || to.name === 'Wifi' || to.name === 'DoiTwo' || to.name === 'TimeoutError') {
        store.commit('setIsLoading', false);
        return next();
    }
    store.commit('setIsLoading', true);
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token') ?? localStorage.getItem('token');
    const encryptedMsisdn = urlParams.get('encryptedMsisdn') ?? localStorage.getItem('encryptedMsisdn');
    const campaignId = urlParams.get('campaignId') || urlParams.get('cid');
    const googleClickId = urlParams.get('gclid');
    const clickId = urlParams.get('clickId');

    if (campaignId) setCampaignId(campaignId)
    if (googleClickId) setGoogleClickId(googleClickId)
    if (clickId) setClickId(clickId)
    if (encryptedMsisdn && token) {
        return await storeUserDetails(encryptedMsisdn, token, next);
    } else {
        store.commit('setIsLoading', false);
        return next({name: 'Wifi'});
    }
}

async function userAuthenticateNormalFlow(to, from, next) {
    const urlParams = new URLSearchParams(window.location.search);
    const encryptedMsisdn = urlParams.get('e-msisdn');
    if (store.state.user) {
        store.commit('setIsLoading', false);
        if (rediretFreeUserFromPredictorAndFantasy(to, next)) return;
        return next();
    }
    const userSessionData = getUserSessionData();
    if (userSessionData.msisdn) {
        await setUserData(userSessionData.msisdn, userSessionData.serviceId, next, to);
    }

    const allowedRoutes = ['TermsAndConditions', 'HomeView', 'NewsHome', 'MenuView', 'UpcomingFixtures', 'RequestOtp', 'NewsVideos', 'NewsArticles', 'NewsVideo', 'NewsArticle', 'NewsExclusive'];
    if (allowedRoutes.includes(to.name) && !encryptedMsisdn) return next();
    if (!allowedRoutes.includes(to.name) && !store.state.user) return next({name: 'RequestOtp'});

    const campaignId = urlParams.get('campaignId') || urlParams.get('cid');
    const googleClickId = urlParams.get('gclid');
    const msisdn = urlParams.get('msisdn') ?? localStorage.getItem('msisdn');
    const isMiniApp = urlParams.get('miniApp');
    const clickId = urlParams.get('clickId');

    if (msisdn && isMiniApp) return await miniAppLogin(msisdn, next);
    if (campaignId) setCampaignId(campaignId)
    if (googleClickId) setGoogleClickId(googleClickId)
    if (clickId) setClickId(clickId)
    if (encryptedMsisdn) return await encryptedMsisdnAuth(encryptedMsisdn, next, campaignId);

    store.commit('setIsLoading', false);
    return next();
}

async function miniAppLogin(msisdn, next) {
    localStorage.setItem('isMiniApp', true);
    await getMultiTierService(msisdn);
    return await redirectBasedOnUserStatus(next);
}

function setCampaignId(campaignId) {
    store.commit('setCampaignId', campaignId);
}

function setGoogleClickId(googleClickId) {
    store.commit('setGoogleClickId', googleClickId);
}

function setClickId(clickId) {
    store.commit('setClickId', clickId);
}

async function storeUserDetails(encryptedMsisdn, token, next) {
    const userDataResponse = await getUser(encryptedMsisdn, token);
    await setEncryptedUserData(userDataResponse, token, encryptedMsisdn);
    return await redirectBasedOnUserStatusMarketingFlow(userDataResponse.data, next);
}

async function redirectBasedOnUserStatusMarketingFlow(user, next) {
    //TODO look at this logic !!
    store.commit('setIsLoading', false);
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');

    if (user.status === "Active" && user.serviceId !== ServiceIds.free) return next({name: 'LandingPage'});
    else if (user.status === 'Content Blocked') return next({path: `/error/:${user.status}`})
    // else if (action) return await handleActionClick(user, action, next);
    else return next({name: 'DoiTwo'});
    // Need to implement this page else if(user?.contentBlockedEnabled) return next({name: 'ContentBlockOptIn'});
}

async function getUser(encryptedMsisdn, token) {
    const serviceId = await getMultiTierServiceV2(token);
    return await DcbService.getUserViaEncryptedMsisdn(encryptedMsisdn, token, serviceId);
}

async function setEncryptedUserData(userDataResponse, token = undefined, encryptedMsisdn = undefined) {
    await DcbService.trackLoginV2(token);
    store.commit('setUser', userDataResponse.data);
    store.commit('setToken', token);
    store.commit('setEncryptedMsisdn', encryptedMsisdn);
    localStorage.setItem('token', token);
    localStorage.setItem('encryptedMsisdn', encryptedMsisdn);
}

async function subscriptionIdAuth(subscriptionId, next, campaignId) {
    store.commit('setIsLoading', true);
    const {msisdn} = (await DcbService.getUserViaSubscriptionId(subscriptionId, ServiceIds.dailyInternal)).data ?? {};

    history.replaceState(history.state, '', location.pathname);

    if (!msisdn) {
        store.commit('setIsLoading', false);
        return next({name: 'HomeView'});
    }

    sessionStorage.setItem('msisdn', msisdn);

    if (campaignId) setCampaignId(campaignId)

    await getMultiTierService(msisdn);
    return await redirectBasedOnUserStatus(next, campaignId);
}

async function encryptedMsisdnAuth(encryptedMsisdn, next, campaignId) {
    const {rawMsisdn} = (await DcbService.decryptMsisdn(encryptedMsisdn)).data;
    sessionStorage.setItem(`msisdn`, rawMsisdn)
    await getMultiTierService(rawMsisdn);
    return await redirectBasedOnUserStatus(next, campaignId);
}

async function getMultiTierService(msisdn) {
    let getMultiTierServiceResponse = await DcbService.getUserViaProductId(msisdn, store.state.productId);
    let getServiceIdByDescriptionResponse = getMultiTierServiceResponse.data.serviceId;
    store.commit('setServiceId', getServiceIdByDescriptionResponse);
    store.commit('setMsisdn', msisdn);
}

async function getMultiTierServiceV2(token) {
    const getMultiTierServiceResponse = await DcbService.getMultiTierServiceV2(token);
    const getServiceIdByDescriptionResponse = getServiceIdByDescription(getMultiTierServiceResponse.data);
    setServiceIdValues(getServiceIdByDescriptionResponse);
    return getServiceIdByDescriptionResponse
}

function setServiceIdValues(serviceId) {
    localStorage.setItem('serviceId', serviceId);
    store.commit('setServiceId', serviceId);
}

async function redirectBasedOnUserStatus(next, campaignId) {
    const user = (await DcbService.getUserViaProductId(store.state.msisdn, store.state.productId)).data;
    store.commit('setUser', user);
    store.commit('setIsLoading', false);
    if (!user) return next({name: 'WelcomeView'});
    if (campaignId) {
        if (`${campaignId}` === `122` && !hasGameAccess(store.state.user)) {
            next({name: 'ConfirmSubscription', params: {price: `5`, interval: 'day'}, replace: true});
        } else return next({name: 'HomeView'});
    } else return next({name: 'HomeView'});
}

function getServiceIdByDescription(array) {
    return array[0].Description;
}

function noUserRoutingIsAllowed(to) {
    if (to.name === 'HomeView') return true
    if (to.name === 'RequestOtp') return true
    if (to.name === 'NewsHome') return true
    if (to.name === 'MenuView') return true
    if (to.name === 'UpcomingFixtures') return true
    return false
}

function rediretFreeUserFromPredictorAndFantasy(to, next) {
    const isRestrictedPath = to.path.includes('tournaments') || to.path.includes('predictor') || to.path.includes('fantasy');
    if (!isRestrictedPath) return false;
    if (!store.state.user) { next({name: 'RequestOtp'}); return true; }
    if (!hasGameAccess(store.state.user)) { next({name: 'MenuView'}); return true; }
    return false;
}

async function setUserData(msisdn, serviceId, next, to) {
    store.commit('setMsisdn', msisdn);
    store.commit('setServiceId', serviceId);
    const user = (await DcbService.getUserViaProductId(msisdn, store.state.productId)).data;
    store.commit('setUser', user);
    store.commit('setIsLoading', false);
    if (rediretFreeUserFromPredictorAndFantasy(to, next)) return;
    next();
}
